// Data Loading// Data Effects for PracticeTest
// Extracted to reduce main file size and prevent Cursor crashes

import { useEffect } from 'react';
import { PracticeTestState, Action } from './PracticeTest.reducer';
import type { Question } from '../../types';

import { logger } from '../../utils/logger';
import { questionLoader } from '../../services/questionLoader';
import { sanitizeQuestionsMedia } from '../../question_data/animations/animationPlaybackRegistry';
import { processQuestions } from '../../utils/questionShuffler';
import { questionTracker } from '../../services/questionTracker';
import { advancedCache } from '../../services/advancedCache';
import { serviceWorkerManager } from '../../services/serviceWorkerManager';
import { prefetchNextTestOnStart } from '../../utils/intelligentPrefetch';
import { preloadNextTest as smartPreloadNextTest } from '../../utils/smartPrefetch';
import { notificationService } from '../../services/notificationService';
import { aiAdaptiveLearningService } from '../../services/aiAdaptiveLearningService';
import { startTransition } from 'react';
import { TEST_QUESTION_MAP, QUESTIONS_PER_TEST } from './PracticeTest.constants';
import { aiCoach } from '../../services/aiCoach';
import { incorrectAnswerTracker } from '../../services/incorrectAnswerTracker';
import { unifiedState } from '../../services/unifiedStateManager';
import { clearCoachingCache } from '../../services/questionCoaching';
import { createSessionToken } from '../../utils/asyncGuard';
import { EffectsContext } from './PracticeTest.effects.types';
import { orderQuestionsForNewUser, logMediaFirstOrdering } from '../../services/questionOrdering';

// PRACTICE ONLY: questionLoader() may run BEFORE test start. questionLoader() must NEVER run during
// an active practice test (session.isFrozen). Cache refreshes and top-ups are deferred until after test end.

/** Remove animationId when the registry has no video so static imageUrl (e.g. Supabase) still shows. */
const sanitizePracticePool = (pool: Question[]): Question[] => sanitizeQuestionsMedia(pool);

const dedupeById = (items: Question[]): Question[] => {
  const seen = new Set<string>();
  const unique: Question[] = [];
  for (const item of items) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    unique.push(item);
  }
  return unique;
};

const topUpToTen = (selected: Question[], pool: Question[]): Question[] => {
  const TARGET = 10;
  const uniqueSelected = dedupeById(selected);
  if (uniqueSelected.length >= TARGET) {
    return uniqueSelected.slice(0, TARGET);
  }
  const needed = TARGET - uniqueSelected.length;
  if (pool.length === 0) {
    return uniqueSelected;
  }
  const selectedIds = new Set(uniqueSelected.map(q => q.id));
  const candidates = dedupeById(pool).filter(q => !selectedIds.has(q.id));
  if (candidates.length === 0) {
    // Never duplicate one question repeatedly just to reach 10.
    return uniqueSelected;
  }
  const shuffled = [...candidates].sort(() => Math.random() - 0.5);
  const extras = shuffled.slice(0, needed);
  return [...uniqueSelected, ...extras];
};

// Hook: Load Questions
export const useLoadQuestions = (
  context: EffectsContext
) => {
  const { testId, currentLanguage, state, questions, setQuestions, dispatch, getTranslatedTestNameForId, getNextTest, sessionFrozenRef, isAliveRef, onQuestionsLoaded } = context;

  const setQuestionsPayload = (q: Question[]) =>
    testId !== 'mock-exam' ? { questions: q, isPractice: true as const } : q;

  useEffect(() => {
    // PRODUCTION GUARANTEE: Never load or replace questions during an active practice session
    // This is the CRITICAL defense against audio/mute/skip triggering reloads
    const hasFrozenSession = state.session?.isFrozen === true && (state.session.sessionQuestions?.length ?? 0) > 0;
    if (hasFrozenSession) {
      logger.lifecycle('PracticeTest', 'load_questions_blocked_frozen_session', { testId });
      return;
    }
    if (sessionFrozenRef?.current && questions.length > 0) {
      logger.lifecycle('PracticeTest', 'load_questions_blocked_session_frozen', { testId });
      return;
    }

    // PRODUCTION GUARANTEE: Additional safety - prevent loading during answer input
    if (state.answerInputDisabledUntil > Date.now()) {
      logger.lifecycle('PracticeTest', 'load_questions_blocked_input_disabled', { testId });
      return;
    }

    const isTargeted = typeof window !== 'undefined'
      && new URLSearchParams(window.location.search).get('targeted') === 'true';
    const getTargetedIds = () => (testId ? incorrectAnswerTracker.getIncorrectQuestionIds(testId) : []);
    const focusAreaIds = new Set(unifiedState.getFocusAreas().map(area => area.testId));
    const shouldFocusOnly = !!(testId && focusAreaIds.size > 0 && !focusAreaIds.has(testId));
    const applyFocusOnly = (items: Question[]) => {
      if (!shouldFocusOnly) return items;
      const ids = getTargetedIds();
      if (ids.length === 0) return items;
      const filtered = items.filter(q => ids.includes(q.id));
      return filtered.length > 0 ? filtered : items;
    };
    const applyTargeted = (items: Question[]) => {
      if (!isTargeted) return items;
      const ids = getTargetedIds();
      if (ids.length === 0) return items;
      const filtered = items.filter(q => ids.includes(q.id));
      return filtered.length > 0 ? filtered : items;
    };
    // Select up to QUESTIONS_PER_TEST randomly from pool. Different 10 each test load/retake.
    const selectRandomQuestions = (pool: Question[], count: number = QUESTIONS_PER_TEST): Question[] => {
      if (!testId || testId === 'mock-exam') return pool;
      const shuffled = dedupeById(pool).sort(() => Math.random() - 0.5);
      return shuffled.slice(0, count);
    };
    // INSTANT LOADING: Check cache first (fast, usually <10ms)
    // Only show "Translating questions..." if cache miss (slow load)
    let showLoadingTimeout: NodeJS.Timeout | null = null;
    
    const loadQuestions = async () => {
      if (!testId) return;

      // PRODUCTION GUARANTEE: Block re-entry with multiple safety checks
      if (sessionFrozenRef?.current && questions.length > 0) {
        logger.lifecycle('PracticeTest', 'load_questions_aborted_session_frozen', { testId });
        return;
      }

      // PRODUCTION GUARANTEE: Skip loading if we already have questions and test is complete
      if (state.testComplete && questions.length > 0) {
        logger.lifecycle('PracticeTest', 'load_questions_skipped_test_complete', { testId });
        return;
      }

      // Clear coaching cache so this test gets fresh follow-ups (avoids same prompts across questions)
      clearCoachingCache();

      // CRITICAL: Use synchronous question loader as INSTANT path (English only).
      // nl/ar must NOT use this path — it is English-only; translated content comes from questionLoader.
      const lang = (currentLanguage || 'nl') as 'en' | 'nl' | 'ar';
      const syncQuestionLoader = TEST_QUESTION_MAP[testId];
      if (lang === 'en' && syncQuestionLoader) {
        try {
          // Load questions synchronously from question data (instant, no async delay)
          const syncQuestions = sanitizePracticePool(syncQuestionLoader());
          if (syncQuestions && syncQuestions.length > 0) {
            const afterFocus = applyFocusOnly(syncQuestions);
            const afterTargeted = applyTargeted(afterFocus);
            const afterLimit = topUpToTen(selectRandomQuestions(afterTargeted), syncQuestions);

            // Apply media-first ordering for new users in specific topics
            const mediaFirstQuestions = await orderQuestionsForNewUser(afterLimit, testId);

            // Log media-first ordering if applied
            const mediaCount = mediaFirstQuestions.filter(q => q.imageUrl && q.imageUrl.trim()).length;
            if (mediaCount > 0 && mediaFirstQuestions.length > mediaCount) {
              logMediaFirstOrdering(testId, mediaFirstQuestions.length, mediaCount);
            }

            const processedQuestions = processQuestions(mediaFirstQuestions, false, false);
            // CRITICAL: Set questions SYNCHRONOUSLY for instant UI
            createSessionToken();
            setQuestions(processedQuestions);
            dispatch({ type: 'SET_QUESTIONS', payload: setQuestionsPayload(processedQuestions) } as Action);
            dispatch({ type: 'SET_TRANSLATING', payload: false });
            sessionFrozenRef.current = true;
            onQuestionsLoaded?.(processedQuestions);
            console.log('[QUESTIONS] loaded', {
              count: processedQuestions.length,
              firstId: processedQuestions?.[0]?.id,
              firstOptions: processedQuestions?.[0]?.options?.map(o => o.id)
            });
            logger.lifecycle('PracticeTest', 'questions_loaded', { testId, count: processedQuestions.length, source: 'sync' });

            // Cache must NOT replace questions while test is in progress (deferred until after test end).
            advancedCache.getCachedQuestions(testId, currentLanguage || 'nl')
              .then(asyncCached => {
                // PRODUCTION GUARANTEE: Never replace questions during frozen session
                if (sessionFrozenRef?.current) {
                  logger.lifecycle('PracticeTest', 'cache_update_blocked_frozen_session', { testId });
                  return;
                }
                if (asyncCached && asyncCached.length >= 10) {
                  const sanitizedCached = sanitizePracticePool(asyncCached);
                  const trackedIds = questionTracker.getTrackedQuestionIds();
                  const unseenQuestions = sanitizedCached.filter((q: any) => !trackedIds.has(q.id));
                  const questionsToUse = unseenQuestions.length >= 10 ? unseenQuestions : sanitizedCached;
                  if (questionsToUse.length > 0) {
                    const pool = applyTargeted(applyFocusOnly(questionsToUse));
                    const selectedQuestions = topUpToTen(selectRandomQuestions(pool), pool);

                    // Apply media-first ordering for new users
                    orderQuestionsForNewUser(selectedQuestions, testId).then(mediaFirstQuestions => {
                      const processed = processQuestions(mediaFirstQuestions, false, false);
                      if (!isAliveRef?.current) return;
                      setQuestions(processed);
                      dispatch({ type: 'SET_QUESTIONS', payload: setQuestionsPayload(processed) } as Action);
                    });
                  }
                }
              })
              .catch(() => {
                // Ignore errors - we already have questions from sync loader
              });
            
            // Defer prefetching to background
            if (testId !== 'mock-exam') {
              if ('requestIdleCallback' in window) {
                (window as any).requestIdleCallback(() => {
                  prefetchNextTestOnStart(testId, currentLanguage || 'nl');
                });
              }
            }
            return; // Exit early - UI is now interactive!
          }
        } catch {
          // Sync loader failed, continue to async cache check
        }
      }
      
      // PERFORMANCE OPTIMIZATION: Show first question immediately (Netflix pattern)
      // Try to load from cache first (fast path) - async fallback
      try {
        const rawCached = await advancedCache.getCachedQuestions(testId, (currentLanguage || 'nl') as 'en' | 'nl' | 'ar');
        const cachedQuestions =
          rawCached && rawCached.length > 0 ? sanitizePracticePool(rawCached) : rawCached;
        if (cachedQuestions && cachedQuestions.length >= 10) {
          // Cache hit - show first question immediately, load rest in background
          const trackedIds = questionTracker.getTrackedQuestionIds();
          const unseenQuestions = cachedQuestions.filter(q => !trackedIds.has(q.id));
          const questionsToUse = unseenQuestions.length >= 10 ? unseenQuestions : cachedQuestions;
          
          // Load all questions immediately (no background delay)
          if (questionsToUse.length > 0) {
            const pool = applyTargeted(applyFocusOnly(questionsToUse));
            const selectedQuestions = topUpToTen(selectRandomQuestions(pool), pool);

            // Apply media-first ordering for new users
            orderQuestionsForNewUser(selectedQuestions, testId).then(mediaFirstQuestions => {
              const processedQuestions = processQuestions(mediaFirstQuestions, false, false);
              setQuestions(processedQuestions);
              dispatch({ type: 'SET_QUESTIONS', payload: setQuestionsPayload(processedQuestions) } as Action);
              dispatch({ type: 'SET_TRANSLATING', payload: false });
              sessionFrozenRef.current = true;
              onQuestionsLoaded?.(processedQuestions);
              logger.debug(`✅ Cache hit: Loaded all ${processedQuestions.length} questions for ${testId}`);
            });
            
            // Defer prefetching to background (non-blocking)
            if (testId !== 'mock-exam') {
              if ('requestIdleCallback' in window) {
                (window as any).requestIdleCallback(() => {
                  prefetchNextTestOnStart(testId, currentLanguage || 'nl');
                });
              } else {
                setTimeout(() => {
                  prefetchNextTestOnStart(testId, currentLanguage || 'nl');
                }, 0);
              }
            }
          } else {
            const pool = applyTargeted(cachedQuestions);
            const processedQuestions = processQuestions(topUpToTen(selectRandomQuestions(pool), pool), false, false);
            if (!isAliveRef?.current) return;
            setQuestions(processedQuestions);
            dispatch({ type: 'SET_QUESTIONS', payload: setQuestionsPayload(processedQuestions) } as Action);
            dispatch({ type: 'SET_TRANSLATING', payload: false });
            sessionFrozenRef.current = true;
            onQuestionsLoaded?.(processedQuestions);
            // Defer debug logging to background (non-blocking)
            if ('requestIdleCallback' in window && process.env.NODE_ENV === 'development') {
              (window as any).requestIdleCallback(() => {
                // Cache fallback loaded successfully
              });
            }
            logger.debug(`✅ Cache fallback: Loaded all ${processedQuestions.length} questions for ${testId}`);
          }
          
          // Preload next test in background
          if (testId !== 'mock-exam' && getNextTest && typeof getNextTest === 'function') {
            try {
              prefetchNextTestOnStart(testId, currentLanguage || 'nl');
              smartPreloadNextTest(testId, () => {
                try {
                  return getNextTest();
                } catch (err) {
                  logger.debug('getNextTest failed:', err);
                  return null;
                }
              });
            } catch (error) {
              // Silently fail - prefetching is non-critical
              logger.debug('Prefetch failed:', error);
            }
          }
          return; // Exit early - first question shown instantly
        }
      } catch (cacheError) {
        logger.debug('Cache check failed, loading from source:', cacheError);
      }
      
      // Cache miss - show loading state after short delay (only if loading takes time)
      showLoadingTimeout = setTimeout(() => {
        dispatch({ type: 'SET_TRANSLATING', payload: true });
      }, 100); // Only show loading if it takes >100ms

      try {
        const questions = sanitizePracticePool(
          await questionLoader.loadQuestions(
            testId,
            (currentLanguage || 'nl') as 'en' | 'nl' | 'ar',
            { useCache: true, saveToCache: true }
          )
        );
        
        // Clear loading timeout since we're loading now
        if (showLoadingTimeout) {
          clearTimeout(showLoadingTimeout);
          showLoadingTimeout = null;
        }

        if (questions && questions.length > 0) {
          // Netflix-style: Filter out already-shown questions to prevent repetition
          const trackedIds = questionTracker.getTrackedQuestionIds();
          const unseenQuestions = questions.filter(q => !trackedIds.has(q.id));
          
          // Use unseen questions if available, otherwise use all questions
          // IMPORTANT: If all questions are tracked, reset to use all questions
          let questionsToUse = unseenQuestions.length > 0 ? unseenQuestions : questions;
          
          // Safety check: If questionsToUse is empty but we have questions, use all questions
          if (questionsToUse.length === 0 && questions.length > 0) {
            // Defer debug logging to background (non-blocking)
            if ('requestIdleCallback' in window && process.env.NODE_ENV === 'development') {
              (window as any).requestIdleCallback(() => {
                logger.debug(`All questions tracked for ${testId}, using all questions`, { totalQuestions: questions.length, trackedCount: trackedIds.size });
              });
            }
            questionsToUse = questions;
          }
          
          // Load all questions immediately
          if (questionsToUse.length > 0) {
            // Process all questions
            const pool = applyTargeted(questionsToUse);
            const processedQuestions = processQuestions(topUpToTen(selectRandomQuestions(pool), pool), false, false);
            
            if (sessionFrozenRef?.current) return;
            if (!isAliveRef?.current) {
              logger.lifecycle('PracticeTest', 'load_questions_abort', { reason: 'unmounted', testId });
              return;
            }
            createSessionToken();
            setQuestions(processedQuestions);
            dispatch({ type: 'SET_QUESTIONS', payload: setQuestionsPayload(processedQuestions) } as Action);
            dispatch({ type: 'SET_TRANSLATING', payload: false });
            sessionFrozenRef.current = true;
            onQuestionsLoaded?.(processedQuestions);
            console.log('[QUESTIONS] loaded', {
              count: processedQuestions.length,
              firstId: processedQuestions?.[0]?.id,
              firstOptions: processedQuestions?.[0]?.options?.map(o => o.id)
            });
            logger.lifecycle('PracticeTest', 'questions_loaded', { testId, count: processedQuestions.length });
            // Defer debug logging to background (non-blocking)
            if ('requestIdleCallback' in window && process.env.NODE_ENV === 'development') {
              (window as any).requestIdleCallback(() => {
                logger.debug(`Loaded ${processedQuestions.length} questions for ${testId}`, { testId, questionCount: processedQuestions.length, firstQuestion: processedQuestions[0]?.id });
              });
            }
            logger.debug(`✅ Loaded all ${processedQuestions.length} questions for ${testId}`);
            
            // Log and cache operations (deferred, non-blocking)
            setTimeout(() => {
              const imageQuestionsCount = processedQuestions.filter(q => q.imageUrl).length;
              if (imageQuestionsCount > 0) {
                logger.info(`📸 Found ${imageQuestionsCount} image questions out of ${processedQuestions.length} total questions for ${testId}`);
              } else {
                logger.warn(`⚠️ No image questions found in ${processedQuestions.length} questions for ${testId}`);
              }
              logger.debug(`✅ Loaded ${processedQuestions.length} questions for ${testId} (${currentLanguage || 'nl'})`);
              
              // Cache questions in Service Worker for offline use (async, non-blocking)
              if (serviceWorkerManager.isActive()) {
                serviceWorkerManager.cacheQuestions(testId, (currentLanguage || 'nl') as 'en' | 'nl' | 'ar', processedQuestions)
                  .catch(err => {
                    logger.error('Error caching questions in Service Worker:', err);
                  });
              }
            }, 0);
          } else {
            const pool = applyTargeted(questionsToUse);
            const processedQuestions = processQuestions(topUpToTen(selectRandomQuestions(pool), pool), false, false);
            if (sessionFrozenRef?.current) return;
            if (!isAliveRef?.current) return;
            setQuestions(processedQuestions);
            dispatch({ type: 'SET_QUESTIONS', payload: setQuestionsPayload(processedQuestions) } as Action);
            sessionFrozenRef.current = true;
            // Log and cache operations (deferred, non-blocking)
            setTimeout(() => {
              const imageQuestionsCount = processedQuestions.filter(q => q.imageUrl).length;
              if (imageQuestionsCount > 0) {
                logger.info(`📸 Found ${imageQuestionsCount} image questions out of ${processedQuestions.length} total questions for ${testId}`);
              } else {
                logger.warn(`⚠️ No image questions found in ${processedQuestions.length} questions for ${testId}`);
              }
              logger.debug(`✅ Loaded ${processedQuestions.length} questions for ${testId} (${currentLanguage || 'nl'})`);
              
              // Cache questions in Service Worker for offline use (async, non-blocking)
              if (serviceWorkerManager.isActive()) {
                serviceWorkerManager.cacheQuestions(testId, (currentLanguage || 'nl') as 'en' | 'nl' | 'ar', processedQuestions)
                  .catch(err => {
                    logger.error('Error caching questions in Service Worker:', err);
                  });
              }
            }, 0);
          }
          
          // Defer prefetching to background (non-blocking)
          if (testId !== 'mock-exam') {
            if ('requestIdleCallback' in window) {
              (window as any).requestIdleCallback(() => {
                if (testId !== 'mock-exam' && getNextTest && typeof getNextTest === 'function') {
                  try {
                    prefetchNextTestOnStart(testId, currentLanguage || 'nl');
                    smartPreloadNextTest(testId, () => {
                      try {
                        return getNextTest();
                      } catch (err) {
                        logger.debug('getNextTest failed:', err);
                        return null;
                      }
                    });
                  } catch (error) {
                    logger.debug('Prefetch failed:', error);
                  }
                }
              });
            } else {
              setTimeout(() => {
                if (testId !== 'mock-exam' && getNextTest && typeof getNextTest === 'function') {
                  try {
                    prefetchNextTestOnStart(testId, currentLanguage || 'nl');
                    smartPreloadNextTest(testId, () => {
                      try {
                        return getNextTest();
                      } catch (err) {
                        logger.debug('getNextTest failed:', err);
                        return null;
                      }
                    });
                  } catch (error) {
                    logger.debug('Prefetch failed:', error);
                  }
                }
              }, 0);
            }
          }
          
          // Schedule break reminder after 30 minutes of study
          const sessionStartTime = Date.now();
          notificationService.scheduleBreakReminder(sessionStartTime);
          
          if (testId !== 'mock-exam') {
            const testHistory = aiCoach.getTestHistory();
            
            aiAdaptiveLearningService.analyzeLearningPattern(testHistory)
              .then(insight => {
                startTransition(() => {
                  dispatch({ type: 'SET_AI_INSIGHT', payload: insight });
                  dispatch({ type: 'SET_DIFFICULTY_LEVEL', payload: insight.recommendedDifficulty });
                });
                
                const testScores = aiCoach.getTestScores();
                const weakTopics = Object.entries(testScores)
                  .filter(([_, data]) => data.average < 60)
                  .map(([testId, _]) => {
                    // Use translated test names
                    return getTranslatedTestNameForId(testId);
                  });
                
                aiAdaptiveLearningService.recommendQuestions(
                  questions,
                  insight.recommendedDifficulty,
                  questions.length,
                  testHistory,
                  weakTopics.length > 0 ? weakTopics : undefined
                ).then(recommendation => {
                  // CRITICAL: Never replace questions during an active session (audio/mute/skip must not trigger reload)
                  if (sessionFrozenRef?.current) return;
                  if (!isAliveRef?.current) return;
                  if (recommendation.questionIds.length > 0) {
                    const selectedQuestions = questions.filter(q => 
                      recommendation.questionIds.includes(q.id)
                    );
                    if (selectedQuestions.length > 0) {
                      const pool = applyTargeted(selectedQuestions);
                      const processedQuestions = processQuestions(topUpToTen(selectRandomQuestions(pool), pool), false, false);
                      if (sessionFrozenRef?.current) return;
                      if (!isAliveRef?.current) return;
                      setQuestions(processedQuestions);
                      dispatch({ type: 'SET_QUESTIONS', payload: setQuestionsPayload(processedQuestions) } as Action);
                      logger.debug('AI recommended questions loaded:', processedQuestions.length);
                    }
                  }
                }).catch(err => {
                  logger.error('AI question recommendation failed:', err);
                });
              })
              .catch(error => {
                logger.error('AI learning pattern analysis failed:', error);
                dispatch({ type: 'SET_DIFFICULTY_LEVEL', payload: 3 });
              });
          }
        } else {
          logger.warn(`No questions loaded for ${testId}`);
          // Try fallback immediately
          const fallbackLoader = TEST_QUESTION_MAP[testId];
          if (fallbackLoader) {
            try {
              const fallbackQuestions = sanitizePracticePool(fallbackLoader());
              if (fallbackQuestions && fallbackQuestions.length > 0 && !sessionFrozenRef?.current) {
                const pool = applyTargeted(fallbackQuestions);
                const processedQuestions = processQuestions(topUpToTen(selectRandomQuestions(pool), pool), false, false);
                setQuestions(processedQuestions);
                dispatch({ type: 'SET_QUESTIONS', payload: setQuestionsPayload(processedQuestions) } as Action);
                sessionFrozenRef.current = true;
                console.log('[QUESTIONS] loaded (fallback)', {
                  count: processedQuestions.length,
                  firstId: processedQuestions?.[0]?.id,
                  firstOptions: processedQuestions?.[0]?.options?.map(o => o.id)
                });
                logger.debug(`Using fallback English questions for ${testId}`);
              }
            } catch (fallbackError) {
              // Defer error logging to background (non-blocking)
              if ('requestIdleCallback' in window) {
                (window as any).requestIdleCallback(() => {
                  logger.error(`Fallback loading failed for ${testId}:`, fallbackError);
                });
              }
              logger.error('Fallback loading also failed:', fallbackError);
            }
          }
        }
      } catch (error) {
        // Defer error logging to background (non-blocking)
        if ('requestIdleCallback' in window) {
          (window as any).requestIdleCallback(() => {
            logger.error(`Failed to load questions for ${testId}:`, error);
          });
        }
        logger.error(`Failed to load questions for ${testId}:`, error);
        try {
          const fallbackLoader = TEST_QUESTION_MAP[testId];
            if (fallbackLoader && !sessionFrozenRef?.current) {
            const fallbackQuestions = sanitizePracticePool(fallbackLoader());
            if (fallbackQuestions && fallbackQuestions.length > 0) {
              const pool = applyTargeted(fallbackQuestions);
              const processedQuestions = processQuestions(topUpToTen(selectRandomQuestions(pool), pool), false, false);
              if (!isAliveRef?.current) return;
              setQuestions(processedQuestions);
              dispatch({ type: 'SET_QUESTIONS', payload: setQuestionsPayload(processedQuestions) } as Action);
              sessionFrozenRef.current = true;
              console.log('[QUESTIONS] loaded (error fallback)', {
                count: processedQuestions.length,
                firstId: processedQuestions?.[0]?.id,
                firstOptions: processedQuestions?.[0]?.options?.map(o => o.id)
              });
              // Defer debug logging to background (non-blocking)
              if ('requestIdleCallback' in window && process.env.NODE_ENV === 'development') {
                (window as any).requestIdleCallback(() => {
                  logger.debug(`Error fallback loaded ${processedQuestions.length} questions for ${testId}`);
                });
              }
              logger.debug(`Using fallback English questions for ${testId}`);
            } else {
              // Defer error logging to background (non-blocking)
              if ('requestIdleCallback' in window) {
                (window as any).requestIdleCallback(() => {
                  logger.warn(`Fallback loader returned empty array for ${testId}`);
                });
              }
            }
          } else {
            logger.error(`No fallback loader found for ${testId}`);
          }
        } catch (fallbackError) {
          logger.error(`Fallback loading also failed for ${testId}:`, fallbackError);
          logger.error('Fallback loading also failed:', fallbackError);
        }
      } finally {
        // Clear loading timeout if it exists
        if (showLoadingTimeout) {
          clearTimeout(showLoadingTimeout);
        }
        dispatch({ type: 'SET_TRANSLATING', payload: false });
      }
    };

    loadQuestions();
    
    // Cleanup timeout on unmount
    return () => {
      if (showLoadingTimeout) {
        clearTimeout(showLoadingTimeout);
      }
    };
    // DATA LOADING GUARANTEE: Do NOT depend on questions.length – audio/mute/skip/visibility must never trigger reload.
    // Questions loaded ONCE per session; no reloads during active session (sessionFrozenRef guards async paths).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId, currentLanguage, state.testComplete, setQuestions, dispatch, getTranslatedTestNameForId]); 
  // getNextTest removed from deps - it's stable via useCallback but causes infinite loops if included
};

// Hook: Motivation Word Reset
export const useMotivationWordReset = (
  state: PracticeTestState,
  currentQuestionIndex: number,
  motivationWordRef: React.MutableRefObject<string>,
  dispatch: React.Dispatch<Action>
) => {
  useEffect(() => {
    if (!state.isAnswered || !state.selectedAnswer) {
      motivationWordRef.current = '';
      dispatch({ type: 'SET_MOTIVATION_WORD', payload: '' });
    }
  }, [currentQuestionIndex, state.isAnswered, state.selectedAnswer, motivationWordRef, dispatch]);
};

// Hook: Preload Next Test
export const usePreloadNextTest = (
  state: PracticeTestState,
  nextTest: { id: string; name: string } | null,
  _getNextTest: (() => { id: string; name: string } | null) | undefined
) => {
  useEffect(() => {
    // Only run when test is complete and we have a valid next test ID
    if (!state.testComplete || !nextTest?.id) return;
    
    // Use nextTest.id directly instead of calling getNextTest again
    // This avoids potential infinite loops from getNextTest dependency
    try {
      smartPreloadNextTest(nextTest.id, () => {
        // Return the already-calculated nextTest instead of calling getNextTest
        return nextTest;
      });
    } catch (error) {
      logger.debug('Preload failed:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- nextTest excluded to avoid infinite loops
  }, [state.testComplete, nextTest?.id]);
};
